/* eslint-disable max-lines */
import {
  Box,
  Button,
  Flex,
  Link as ChakraLink,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { BigNumber } from '@ethersproject/bignumber';
import { useQuery } from '@tanstack/react-query';
import ApyInfo from '@ui/components/ApyInfo';
import { ApySimpleLineChart } from '@ui/components/ApySimpleLineChart';
import Balance from '@ui/components/Balance';
import { Card } from '@ui/components/Card';
import { EarningSimpleLineChart } from '@ui/components/EarningSimpleLineChart';
import Features from '@ui/components/Features';
import { VnoDrawer } from '@ui/components/GetAssetDrawers';
import { HorizontalScrolling } from '@ui/components/HorizontalScrolling';
import { HorizontalScrollingButton } from '@ui/components/HorizontalScrollingButton';
import Info from '@ui/components/Info';
import { InfoTooltip } from '@ui/components/InfoTooltip';
import { LazyComponent } from '@ui/components/LazyComponent';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import { NFTCard, useNftClaimDrawerStore } from '@ui/components/NFTCard';
import { RotatingBg } from '@ui/components/RotatingBg';
import StakingInfoDrawer from '@ui/components/StakingInfoDrawer';
import { TokenIcon } from '@ui/components/TokenIcon';
import WithWallet from '@ui/components/Wallet/WithWallet';
import { pathDict } from '@ui/config/paths';
import { useHistoricApys } from '@ui/hooks/useApr';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { COIN_MODE } from '@ui/hooks/useCoinMode';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import useDebounce from '@ui/hooks/useDebounce';
import { useIsWhitelistFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTransactionHistory } from '@ui/hooks/useTransactionHistory';
import { useTranslations } from '@ui/i18n';
import Link from '@ui/overrides/next/link';
import { useRouter } from '@ui/overrides/next/router';
import { useGraphQLContext } from '@ui/providers/GraphQLProvider';
import { getTransactionHistoryByDay } from '@ui/queries/history';
import { formatNumber, formatTokenAmount } from '@ui/utils/format';
import { gardenTokenDict } from '@ui/utils/garden';
import getBgImage from '@ui/utils/getBgImageProps';
import { useOwnedDepositCoinAmount, useStrategyApy } from '@ui/utils/strategy';
import { pushRouterWithoutRerender } from '@ui/utils/urlHistory';
import { currentWallet } from '@veno-app/wallet';
import { useEffect, useMemo, useState } from 'react';

import PostMvp from './PostMvp';
import StakingTransactionShortInfo from './StakingTransactionShortInfo';
import StrategyDashboardDrawer, {
  useStrategyDashboardDrawerStore,
} from './StrategyDashboardDrawer';
import useDashboardStakingData from './useDashboardStakingData';

const { useIsConnected, useAccount } = currentWallet;

type StakingProps = {
  coinMode: CoinMode;
  showTransactionHistory: () => void;
};

const Staking = ({ coinMode, showTransactionHistory }: StakingProps) => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const { isOpen: isVnoDrawerOpen, onClose: onVnoDrawerClose } =
    useDisclosure();
  const [includeLpEarnings, setIncludeLpEarnings] = useState(false);
  const {
    liquidBalance,
    redeemableTokenAmount,
    pendingUsdAmount,
    pendingTokenAmount,
    redeemableUsdAmount,
    apy,
    apyLoading,
    stakedUsdValue,
    lcroBalanceUsdValue,
    lcroBalanceUsdValueLoading,
    redeemableNftTokens,
    croStaked,
    lifeTimeEarningsCro,
    pendingEarningsCro,
    lifeTimeEarningsUsd,
    pendingEarningsUsd,
    lifeTimeEarningsLoading,
    grossApy,
    pendingEarningsLoading,
    lifetimeEarnings,
    pendingEarnings,
    dates,
    currentFerroLpBalance,
    currentTLcroBalance,
    currentFerroLpBalanceUsd,
    currentVvsCroLcroBalance,
    currentVvsCroLcroBalanceUsd,
    currentTLcroBalanceUsd,
  } = useDashboardStakingData(includeLpEarnings);

  const { toggle: openStrategyDashboard, isOpen: strategyDashboardOpen } =
    useStrategyDashboardDrawerStore();
  const strategyApy = useStrategyApy();
  const [nftContainer, setNftContainer] = useState<HTMLDivElement | null>(null);
  const openNftClaimAllDrawer = useNftClaimDrawerStore((s) => s.onOpen);
  const account = useAccount();
  const { data: transactions } = useTransactionHistory(account ?? '', 1);

  const ownedDepositAmount = useOwnedDepositCoinAmount();
  const depositAmountUsd = useCoinUsdValue(ownedDepositAmount, 'cro');
  const isStrategyEnabled = useIsWhitelistFeatureEnabled('strategyVault');

  const { query } = useRouter();
  const { tab } = query;

  const { data: apyList } = useHistoricApys();
  const connected = useIsConnected();
  const bgImageProps = connected ? {} : getBgImage('PATTERN_2');
  const [activeElement, setActiveElement] = useState(-1);
  const debouncedActiveElement = useDebounce(activeElement, 500);

  const [stakingInfoDrawerOpen, setStakingInfoDrawerOpen] = useState(false);

  const client = useGraphQLContext().historyClient;

  const tooltipDataUpToDate =
    debouncedActiveElement >= 0 &&
    debouncedActiveElement < dates.length &&
    debouncedActiveElement === activeElement;

  // not expecting to reuse query
  const { data: selectedTransactionData } = useQuery(
    [
      'transaction-short-info',
      account,
      (dates[debouncedActiveElement] ?? new Date()).getTime(),
    ],
    () =>
      getTransactionHistoryByDay(
        client,
        account ?? '',
        dates[debouncedActiveElement].getTime() / 1000,
      ),
    {
      enabled: !!account && tooltipDataUpToDate,
      cacheTime: 60_000,
    },
  );

  const additionalInfo = useMemo(() => {
    if (!selectedTransactionData || !tooltipDataUpToDate) return undefined;
    return (
      <StakingTransactionShortInfo transactionData={selectedTransactionData} />
    );
  }, [selectedTransactionData, tooltipDataUpToDate]);

  useEffect(() => {
    tab === 'strategy' && openStrategyDashboard(true);
  }, [openStrategyDashboard, tab]);

  return (
    <>
      <StakingInfoDrawer
        netApy={`${(apy * 100).toFixed(2)}%`}
        grossApy={`${((grossApy ?? 0) * 100).toFixed(2)}%`}
        isOpen={stakingInfoDrawerOpen}
        close={() => setStakingInfoDrawerOpen(false)}
      />
      <LazyComponent isOpen={strategyDashboardOpen}>
        <StrategyDashboardDrawer />
      </LazyComponent>
      <WithWallet
        my={6}
        connectHint={t('Connect wallet to see your dashboard')}
        bgPos="right -50px"
        minH="572px"
        px={{ base: 4, desktop: 0 }}
        {...bgImageProps}
      >
        <Balance
          title={t('{coin} staked', {
            coin: coinModeInfo.name,
          })}
          amount={croStaked}
          icon={<coinModeInfo.Icon />}
          unitDecimals={coinModeInfo.decimals}
          usdAmount={stakedUsdValue}
          usdDisplayDecimals={2}
          showUsdAmount
        />
        {coinMode === 'cro' && (
          <Flex justify="center">
            {transactions?.transactions.length ? (
              <ChakraLink onClick={showTransactionHistory} color="pink.500">
                {t('Transaction history')}
              </ChakraLink>
            ) : (
              <Box w="full" h="18px" />
            )}
          </Flex>
        )}

        {isStrategyEnabled && coinMode === 'cro' && (
          <Card
            cursor="pointer"
            bgImage={"url('/images/pattern-liquidity.png')"}
            bgSize="cover"
            bgRepeat="no-repeat"
            bgPos="center center"
            m={0}
            p={2.5}
            mt={4}
            onClick={() => {
              openStrategyDashboard();
              pushRouterWithoutRerender(pathDict.cro.strategy.dashboard);
            }}
          >
            <Flex justify="space-between" align="center">
              <Flex flex={1} flexDir="column">
                <Box>{t('Liquidity strategy')}</Box>
                <Flex color="text-light">
                  {t('Your value')}: <TokenIcon token="cro" />
                  {formatTokenAmount(ownedDepositAmount ?? 0, 2)}
                </Flex>
                <Flex color="text-light">
                  {formatTokenAmount(depositAmountUsd.data ?? 0, 2)} USD
                </Flex>
              </Flex>
              <RotatingBg
                w="62px"
                h="62px"
                justify="center"
                alignItems="center"
              >
                <Flex flexDir="column" align="center">
                  <Text textStyle="bodySmall" color="text-light">
                    APY
                  </Text>
                  <Skeleton
                    isLoaded={strategyApy.data !== undefined}
                    textStyle="h4Bold"
                    mt={-0.5}
                  >
                    {formatNumber(
                      (strategyApy.data.strategyApy ?? 0) * 100,
                      0,
                    ) + '%'}
                  </Skeleton>
                </Flex>
              </RotatingBg>
            </Flex>
          </Card>
        )}

        {coinMode === 'cro' &&
          dates &&
          lifetimeEarnings &&
          pendingEarnings &&
          !!dates.length && (
            <EarningSimpleLineChart
              tokenName="CRO"
              mt={{
                base: '31px',
                desktop: '45px',
              }}
              lifeTimeEarnings={lifetimeEarnings.map((earning, i) => ({
                y: earning,
                date: dates[i].getTime(),
              }))}
              pendingEarnings={pendingEarnings.map((earning, i) => ({
                y: earning,
                date: dates[i].getTime(),
              }))}
              onActiveElementChanged={setActiveElement}
              AdditionalInfo={additionalInfo}
            />
          )}
        <Flex direction="column" gap={3} mt="32px">
          {coinMode === 'cro' && (
            <>
              <Info
                icon={(props) => <coinModeInfo.Icon {...props} />}
                title={t('Lifetime earning')}
                value={
                  !lifeTimeEarningsLoading
                    ? `${formatTokenAmount(
                        lifeTimeEarningsCro ?? BigNumber.from(0),
                        2,
                      )}`
                    : undefined
                }
                convertedValue={
                  lifeTimeEarningsUsd !== undefined
                    ? `${formatTokenAmount(lifeTimeEarningsUsd, 2)} USD`
                    : undefined
                }
                convertedValueLoading={lifeTimeEarningsLoading}
              />
              <Info
                icon={(props) => <coinModeInfo.Icon {...props} />}
                title={t('Pending earnings')}
                value={
                  !pendingEarningsLoading
                    ? `${formatTokenAmount(
                        pendingEarningsCro ?? BigNumber.from(0),
                        2,
                      )}`
                    : undefined
                }
                convertedValue={
                  pendingEarningsUsd !== undefined
                    ? `${formatTokenAmount(pendingEarningsUsd, 2)} USD`
                    : undefined
                }
                convertedValueLoading={pendingEarningsLoading}
              />
            </>
          )}
          <Info
            icon={(props) => <coinModeInfo.LiquidIcon {...props} />}
            title={t('{coin} balance', {
              coin: coinModeInfo.liquidName,
            })}
            value={
              liquidBalance
                ? `${formatTokenAmount(
                    liquidBalance,
                    2,
                    coinModeInfo.liquidDecimals,
                  )}`
                : undefined
            }
            convertedValue={
              lcroBalanceUsdValue !== undefined
                ? `${formatTokenAmount(lcroBalanceUsdValue, 2)} USD`
                : undefined
            }
            convertedValueLoading={lcroBalanceUsdValueLoading}
          />
          <Info
            title={
              <>
                {`Ferro ${gardenTokenDict[coinMode].FERRO.name}`}
                <InfoTooltip
                  title={`Ferro ${gardenTokenDict[coinMode].FERRO.name}`}
                  label={
                    <Text
                      px={{ base: 0, desktop: 8 }}
                      py={{ base: 0, desktop: 4 }}
                    >
                      {t(
                        'dashboard-ferro-lp-tip-{lpToken}-{coin}-{liquidCoin}',
                        {
                          lpToken: gardenTokenDict[coinMode].FERRO.name,
                          coin: coinModeInfo.name,
                          liquidCoin: coinModeInfo.liquidName,
                        },
                      )}
                    </Text>
                  }
                  color="text-active"
                  tooltipProps={{
                    shadow: 'card.normal',
                  }}
                />
              </>
            }
            value={
              currentFerroLpBalance !== undefined
                ? `${currentFerroLpBalance.toFixed(2)}`
                : undefined
            }
            convertedValue={
              currentFerroLpBalanceUsd !== undefined
                ? `${formatNumber(currentFerroLpBalanceUsd, 2)} USD`
                : undefined
            }
            convertedValueLoading={currentFerroLpBalanceUsd === undefined}
          />
          {coinMode === 'cro' && (
            <>
              <Features.vvsLpTracking>
                <Info
                  title={
                    <>
                      VVS LCRO-CRO-LP
                      <InfoTooltip
                        title="VVS LCRO-CRO-LP"
                        label={
                          <Text
                            px={{ base: 0, desktop: 8 }}
                            py={{ base: 0, desktop: 4 }}
                          >
                            {t('dashboard-vvs-lp-tip')}
                          </Text>
                        }
                        color="text-active"
                        tooltipProps={{
                          shadow: 'card.normal',
                        }}
                      />
                    </>
                  }
                  value={
                    currentVvsCroLcroBalance !== undefined
                      ? `${currentVvsCroLcroBalance.toFixed(2)}`
                      : undefined
                  }
                  convertedValue={
                    currentVvsCroLcroBalanceUsd !== undefined
                      ? `${formatNumber(currentVvsCroLcroBalanceUsd, 2)} USD`
                      : undefined
                  }
                  convertedValueLoading={
                    currentVvsCroLcroBalanceUsd === undefined
                  }
                />
              </Features.vvsLpTracking>
              <Info
                title={'Tectonic tLCRO'}
                value={
                  currentTLcroBalance !== undefined
                    ? `${currentTLcroBalance.toFixed(2)}`
                    : undefined
                }
                convertedValue={
                  currentTLcroBalanceUsd !== undefined
                    ? `${formatNumber(currentTLcroBalanceUsd, 2)} USD`
                    : undefined
                }
                convertedValueLoading={currentTLcroBalanceUsd === undefined}
              />
            </>
          )}
          <Box>
            <Info
              title={
                <Flex alignItems="center">
                  <Link href={pathDict[coinMode].stake} passHref>
                    <ChakraLink fontSize="inherit" fontWeight="normal">
                      {t('Real Staking APY')}
                    </ChakraLink>
                  </Link>{' '}
                  {/* grossApy could be NaN if apy unknown */}
                  {(grossApy ?? 0) > 0 && apy && grossApy && (
                    <InfoTooltip
                      title={t('Real Staking APY info')}
                      label={
                        <ApyInfo
                          netApy={`${(apy * 100).toFixed(2)}%`}
                          grossApy={`${(grossApy * 100).toFixed(2)}%`}
                        />
                      }
                      color="text-active"
                      tooltipProps={{
                        shadow: 'card.normal',
                        minW: '407px',
                      }}
                    />
                  )}
                </Flex>
              }
              value={
                !apyLoading
                  ? apy === 0
                    ? '--'
                    : `${(apy * 100).toFixed(2)}%`
                  : undefined
              }
            />
            <PostMvp>
              {apyList ? (
                <ApySimpleLineChart
                  mt="2px"
                  apyList={
                    apyList?.map((data) => ({
                      y: data.apy,
                      date: data.date.getTime(),
                    })) || []
                  }
                />
              ) : (
                <Skeleton w="full" h="124px" />
              )}
            </PostMvp>
          </Box>
        </Flex>
        <Text
          mt={{
            base: '48px',
            desktop: '41px',
          }}
          mb="24px"
          textStyle="h3"
        >
          {t('Unstaked {coin}', {
            coin: coinModeInfo.name,
          })}
        </Text>
        <Flex direction="column" gap={3}>
          <Info
            title={
              <Link href={pathDict[coinMode].claim} passHref>
                <ChakraLink fontSize="inherit" fontWeight="normal">
                  {t('Pending {coin}', {
                    coin: coinModeInfo.name,
                  })}
                </ChakraLink>
              </Link>
            }
            icon={(props) => <coinModeInfo.Icon {...props} />}
            value={
              pendingTokenAmount
                ? `${formatTokenAmount(
                    pendingTokenAmount,
                    2,
                    coinModeInfo.decimals,
                  )}`
                : undefined
            }
            convertedValue={
              pendingUsdAmount !== undefined
                ? `${formatTokenAmount(pendingUsdAmount, 2)} USD`
                : undefined
            }
            convertedValueLoading={pendingUsdAmount === undefined}
          />
          <Info
            title={
              <Link href={pathDict[coinMode].claim} passHref>
                <ChakraLink fontSize="inherit" fontWeight="normal">
                  {t('Redeemable {coin}', {
                    coin: coinModeInfo.name,
                  })}
                </ChakraLink>
              </Link>
            }
            icon={(props) => <coinModeInfo.Icon {...props} />}
            value={
              redeemableTokenAmount
                ? `${formatTokenAmount(
                    redeemableTokenAmount,
                    2,
                    coinModeInfo.decimals,
                  )}`
                : undefined
            }
            convertedValue={
              redeemableUsdAmount !== undefined
                ? `${formatTokenAmount(redeemableUsdAmount, 2)} USD`
                : undefined
            }
            convertedValueLoading={redeemableUsdAmount === undefined}
          />
        </Flex>
        {!!redeemableNftTokens.length && (
          <>
            <Button
              size="sm"
              mt="4px"
              onClick={() => {
                openNftClaimAllDrawer({
                  coinMode,
                  nfts: redeemableNftTokens,
                });
              }}
            >
              {t('Claim all')}
            </Button>
            <InDesktop>
              <HorizontalScrollingButton
                scrollContainer={nftContainer}
                justify="end"
                mt="16px"
              />
            </InDesktop>
            <HorizontalScrolling
              ref={setNftContainer}
              mx={{
                base: '-16px',
                desktop: '-24px',
              }}
              mt="10px"
            >
              {redeemableNftTokens.map((token, i) => {
                return <NFTCard key={i} coinMode={coinMode} nft={token} />;
              })}
            </HorizontalScrolling>
          </>
        )}
      </WithWallet>
      <VnoDrawer isOpen={isVnoDrawerOpen} onClose={onVnoDrawerClose} />
    </>
  );
};
export default Staking;
